import React, { useState } from 'react';
import AccessibleModal from './AccessibleModal';
import { Plus, Users, MoreVertical, ArrowLeft, Settings, Trash2, User } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { teamService } from '../lib/services';
import KanbanBoard from './KanbanBoard';
import EmployeeProfile from './EmployeeProfile';
import type { Team, TeamMember, Replica } from '../types/api';

// Create a local extended type for display purposes if needed
interface DisplayMember extends TeamMember {
    name: string;
    avatar?: string;
    isReplica: boolean;
}

interface TeamsViewProps {
    onNavigate: (view: string) => void;
    selectedTeamId?: string;
}

export default function TeamsView({ onNavigate, selectedTeamId }: TeamsViewProps) {
    const { teams, currentWorkspace, createTeam, refreshTeams } = useWorkspace();
    const [showNewTeam, setShowNewTeam] = useState(false);
    const [newTeamName, setNewTeamName] = useState('');
    const [newTeamDescription, setNewTeamDescription] = useState('');
    const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
    const [isCreating, setIsCreating] = useState(false);

    // Profile Modal State
    const [selectedReplica, setSelectedReplica] = useState<any | null>(null);

    // Check if viewing a specific team
    const viewingTeam = selectedTeamId
        ? teams.find(t => t.id === selectedTeamId) || selectedTeam
        : selectedTeam;

    const handleCreateTeam = async () => {
        if (!newTeamName.trim()) return;

        setIsCreating(true);
        const team = await createTeam(newTeamName.trim(), newTeamDescription.trim());
        if (team) {
            setShowNewTeam(false);
            setNewTeamName('');
            setNewTeamDescription('');
            setSelectedTeam(team);
        }
        setIsCreating(false);
    };

    const handleArchiveTeam = async (teamId: string) => {
        if (confirm('Are you sure you want to archive this team?')) {
            await teamService.update(teamId, { status: 'ARCHIVED' });
            setSelectedTeam(null);
            refreshTeams();
        }
    };

    // Mock members including replicas for demonstration
    // In production, these would come from viewingTeam.members with populated replica data
    const mockMembers: DisplayMember[] = [
        { id: '1', teamId: '1', role: 'Team Lead', userId: 'human1', isReplica: false, name: 'Essam (You)', createdAt: new Date().toISOString() },
        { id: '2', teamId: '1', role: 'Content Strategist', replicaId: 'rep1', isReplica: true, name: 'Sarah (AI)', createdAt: new Date().toISOString() },
        { id: '3', teamId: '1', role: 'Data Analyst', replicaId: 'rep2', isReplica: true, name: 'DataOne (AI)', createdAt: new Date().toISOString() },
    ];

    const handleMemberClick = (member: DisplayMember) => {
        if (member.isReplica) {
            // Transform to Replica type for profile view
            setSelectedReplica({
                id: member.replicaId!,
                name: member.name,
                category: member.role,
                role: member.role,
                status: 'ACTIVE',
                // other fields mocked
            });
        }
    };

    // Show single team view
    if (viewingTeam) {
        return (
            <div className="space-y-6">
                {/* Team Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => {
                                setSelectedTeam(null);
                                onNavigate('teams');
                            }}
                            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                            aria-label="Back to teams list"
                        >
                            <ArrowLeft size={20} />
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-white">{viewingTeam.name}</h1>
                            {viewingTeam.description && (
                                <p className="text-slate-400 mt-1">{viewingTeam.description}</p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors" aria-label="Team settings">
                            <Settings size={18} />
                        </button>
                        <button
                            onClick={() => handleArchiveTeam(viewingTeam.id)}
                            className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded-lg transition-colors"
                            aria-label="Archive team"
                        >
                            <Trash2 size={18} />
                        </button>
                    </div>
                </div>

                {/* Team Stats */}
                <div className="grid grid-cols-3 gap-4">
                    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                        <p className="text-2xl font-bold text-white">{viewingTeam._count?.members || mockMembers.length}</p>
                        <p className="text-sm text-slate-400">Members</p>
                    </div>
                    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                        <p className="text-2xl font-bold text-white">{viewingTeam._count?.tasks || 0}</p>
                        <p className="text-sm text-slate-400">Tasks</p>
                    </div>
                    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                        <p className="text-2xl font-bold text-white capitalize">{viewingTeam.status.toLowerCase()}</p>
                        <p className="text-sm text-slate-400">Status</p>
                    </div>
                </div>

                {/* Team Members List */}
                <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                    <h2 className="text-lg font-semibold text-white mb-4">Team Members</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {mockMembers.map((member) => (
                            <div
                                key={member.id}
                                onClick={() => handleMemberClick(member)}
                                role={member.isReplica ? 'button' : undefined}
                                tabIndex={member.isReplica ? 0 : undefined}
                                onKeyDown={member.isReplica ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleMemberClick(member); } } : undefined}
                                className={`flex items-center justify-between p-3 rounded-lg border border-slate-700 hover:bg-slate-700/50 transition-colors ${member.isReplica ? 'cursor-pointer hover:border-sky-500/50' : ''}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${member.isReplica ? 'bg-gradient-to-br from-indigo-500 to-purple-500' : 'bg-slate-600'}`}>
                                        {member.name.charAt(0)}
                                    </div>
                                    <div>
                                        <p className="font-medium text-white">{member.name}</p>
                                        <p className="text-xs text-slate-400">{member.role}</p>
                                    </div>
                                </div>
                                {member.isReplica && (
                                    <div className="px-2 py-1 rounded bg-sky-500/10 text-sky-400 text-xs border border-sky-500/20">
                                        Replica
                                    </div>
                                )}
                            </div>
                        ))}

                        <button className="flex items-center justify-center p-3 rounded-lg border border-slate-700 border-dashed text-slate-400 hover:text-white hover:border-slate-500 cursor-pointer transition-colors w-full">
                            <Plus size={20} className="mr-2" /> Add Member
                        </button>
                    </div>
                </div>

                {/* Kanban Board */}
                <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
                    <h2 className="text-lg font-semibold text-white mb-4">Task Board</h2>
                    <KanbanBoard teamId={viewingTeam.id} />
                </div>

                {/* Profile Modal */}
                {selectedReplica && (
                    <EmployeeProfile
                        replica={selectedReplica as Replica}
                        onClose={() => setSelectedReplica(null)}
                    />
                )}
            </div>
        );
    }

    // Show teams list
    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white">Teams</h1>
                    <p className="text-slate-400 mt-1">
                        Manage your teams and AI replicas.
                    </p>
                </div>
                <button
                    onClick={() => setShowNewTeam(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-sky-500 hover:bg-sky-400 text-white rounded-lg transition-colors"
                >
                    <Plus size={18} />
                    New Team
                </button>
            </div>

            {/* New Team Modal */}
            <AccessibleModal
                open={showNewTeam}
                onClose={() => { setShowNewTeam(false); setNewTeamName(''); setNewTeamDescription(''); }}
                title="Create New Team"
                panelClassName="bg-slate-800 rounded-xl p-6 w-full max-w-md border border-slate-700"
            >
                <h2 className="text-xl font-semibold text-white mb-4">Create New Team</h2>
                <div className="space-y-4">
                    <div>
                        <label htmlFor="new-team-name" className="block text-sm text-slate-400 mb-1">Team Name</label>
                        <input
                            id="new-team-name"
                            type="text"
                            value={newTeamName}
                            onChange={(e) => setNewTeamName(e.target.value)}
                            placeholder="e.g., Marketing Team"
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-sky-500"
                        />
                    </div>
                    <div>
                        <label htmlFor="new-team-description" className="block text-sm text-slate-400 mb-1">Description (optional)</label>
                        <textarea
                            id="new-team-description"
                            value={newTeamDescription}
                            onChange={(e) => setNewTeamDescription(e.target.value)}
                            placeholder="What does this team do?"
                            rows={3}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-sky-500 resize-none"
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <button
                        onClick={() => {
                            setShowNewTeam(false);
                            setNewTeamName('');
                            setNewTeamDescription('');
                        }}
                        className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleCreateTeam}
                        disabled={!newTeamName.trim() || isCreating}
                        className="px-4 py-2 bg-sky-500 hover:bg-sky-400 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isCreating ? 'Creating...' : 'Create Team'}
                    </button>
                </div>
            </AccessibleModal>

            {/* Teams Grid */}
            {teams.length === 0 ? (
                <div className="bg-slate-800 rounded-xl border border-slate-700 p-12 text-center">
                    <Users size={48} className="mx-auto text-slate-600 mb-4" />
                    <h3 className="text-lg font-medium text-white mb-2">No teams yet</h3>
                    <p className="text-slate-400 mb-6">Create your first team to start collaborating with AI replicas.</p>
                    <button
                        onClick={() => setShowNewTeam(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500 hover:bg-sky-400 text-white rounded-lg transition-colors"
                    >
                        <Plus size={16} />
                        Create Team
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {teams.map((team) => (
                        <div
                            key={team.id}
                            onClick={() => setSelectedTeam(team)}
                            className="bg-slate-800 rounded-xl border border-slate-700 p-5 hover:border-slate-600 cursor-pointer transition-colors group"
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                                    <Users size={22} className="text-white" />
                                </div>
                                <button className="p-1 text-slate-400 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity" aria-label={`More options for ${team.name}`}>
                                    <MoreVertical size={16} />
                                </button>
                            </div>
                            <h3 className="text-lg font-semibold text-white mb-1">{team.name}</h3>
                            {team.description && (
                                <p className="text-sm text-slate-400 line-clamp-2 mb-3">{team.description}</p>
                            )}
                            <div className="flex items-center gap-4 text-sm text-slate-400">
                                <span>{team._count?.members || 0} members</span>
                                <span>·</span>
                                <span>{team._count?.tasks || 0} tasks</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

